import { Flex, FormStatus, H4, P, Space, Table, Th, Tr } from '@dnb/eufemia';
import { Form, Iterate, Wizard } from '@dnb/eufemia/extensions/forms';
import { ApiDto } from '@portals/shared/portal/ApiDto';
import { AppApiEntitlementRequestSchemaV2Form } from '@portals/shared/portal/AppApiEntitlementDto';
import { useMemo } from 'react';

import Divider from '@/components/Divider';
import { apiOrEvent } from '@/utils/integrationServiceUtils';

import ScopeEnvSelectItem from './ScopeEnvSelectItem';

type EditScopesProps = {
  api: ApiDto;
  formId: string;
  initialLiveScopes: Set<string>;
};

export default function EditScopes({ api, formId, initialLiveScopes }: EditScopesProps) {
  const { data } = Form.useData<AppApiEntitlementRequestSchemaV2Form>(formId);

  const hasRemovedNonRequestableLiveScope = useMemo(() => {
    if (api.availableInLiveMode) return false;
    const selectedLiveScopes = new Set(data.scopes.filter((scope) => scope.live).map((scope) => scope.name));
    return [...initialLiveScopes].some((scope) => !selectedLiveScopes.has(scope));
  }, [api.availableInLiveMode, data.scopes, initialLiveScopes]);

  return (
    <Form.Card>
      <H4>{api.name}</H4>
      <P top="small">{api.description}</P>
      <Space bottom="x-small" top="x-small">
        <Divider />
      </Space>

      <Table>
        <thead>
          <Tr>
            <Th style={{ width: '100%' }}>Scope</Th>
            <Th style={{ minWidth: 90 }}>Test</Th>
            <Th style={{ minWidth: 90 }}>Live</Th>
          </Tr>
        </thead>
        <tbody>
          <Iterate.Array path="/scopes" withoutFlex>
            {(scope, itemIndex) => {
              return (
                <ScopeEnvSelectItem
                  apiType={api.type}
                  initiallyHadLiveAccess={initialLiveScopes.has(scope.name)}
                  itemIndex={itemIndex}
                  liveAccessEnabled={api.availableInLiveMode}
                />
              );
            }}
          </Iterate.Array>
        </tbody>
      </Table>

      {hasRemovedNonRequestableLiveScope && (
        <FormStatus state="warning">
          Live access requests have been disabled for this {apiOrEvent(api.type)}. Once an existing live access is
          removed, it cannot be requested again.
        </FormStatus>
      )}

      <Flex.Horizontal>
        <Wizard.PreviousButton />
        <Wizard.NextButton disabled={!data.scopes.some((scope) => scope.test || scope.live)} />
      </Flex.Horizontal>
    </Form.Card>
  );
}
